import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle, ArrowRight, Cpu, Building2, Factory, Users, Zap, Cloud } from 'lucide-react';
import { renewableServices as mockServices } from '../data/mock';
import { contentAPI } from '../services/api';

const RenewableServiceDetail = () => {
  const { slug } = useParams();
  const [services, setServices] = useState(mockServices);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    contentAPI.get('renewable_services').then(res => {
      if (Array.isArray(res.data) && res.data.length) setServices(res.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [slug]);

  const service = useMemo(() => services.find(s => s.slug === slug), [services, slug]);
  const otherServices = useMemo(() => services.filter(s => s.id !== service?.id).slice(0, 3), [services, service]);

  if (loading) return <div className="min-h-screen pt-20 flex items-center justify-center"><p>Loading...</p></div>;

  if (!service) {
    return <Navigate to="/renewable-consulting" replace />;
  }

  const iconMap = {
    Cpu: Cpu,
    Building2: Building2,
    Factory: Factory,
    Users: Users,
    Zap: Zap,
    Cloud: Cloud
  };

  const Icon = iconMap[service.icon];

  return (
    <div className="min-h-screen pt-20">
      {/* Back Button */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Link
            to="/renewable-consulting"
            className="inline-flex items-center text-gray-600 hover:text-emerald-600 transition-colors duration-200"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Renewable Consulting
          </Link>
        </div>
      </div>

      {/* Hero */}
      <section className="relative h-96 flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={service.image}
            alt={service.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/95 to-gray-900/70"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-16 h-16 bg-emerald-600 rounded-xl flex items-center justify-center">
              <Icon className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white">{service.title}</h1>
          </div>
          <p className="text-xl text-gray-200 max-w-2xl">{service.description}</p>
        </div>
      </section>

      {/* Content */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-10">
              {/* Description */}
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">About This Service</h2>
                <p className="text-gray-700 text-lg leading-relaxed">
                  {service.fullDescription}
                </p>
              </div>

              {/* Key Deliverables */}
              <div className="bg-gradient-to-br from-emerald-50 to-blue-50 rounded-2xl p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                  <CheckCircle className="w-7 h-7 text-emerald-600" />
                  Key Deliverables
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {service.deliverables.map((item) => (
                    <div key={item} className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-emerald-600 rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-gray-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Process */}
              {service.process && (
                <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">Our Process</h2>
                  <div className="space-y-4">
                    {service.process.map((step, index) => (
                      <div key={step} className="flex gap-4">
                        <div className="flex-shrink-0 w-8 h-8 bg-emerald-600 text-white rounded-full flex items-center justify-center font-semibold text-sm">
                          {index + 1}
                        </div>
                        <div className="flex-1 pt-1">
                          <p className="text-gray-700 leading-relaxed">{step}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Benefits */}
              <div className="bg-gradient-to-br from-emerald-600 to-emerald-700 rounded-2xl p-8 text-white shadow-xl">
                <h2 className="text-2xl font-bold mb-4">Why Choose This Service?</h2>
                <p className="text-emerald-50 text-lg leading-relaxed">{service.benefits}</p>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 sticky top-24">
                <h3 className="text-xl font-bold text-gray-900 mb-6">Ready to Get Started?</h3>
                
                <div className="space-y-6 mb-8">
                  <div>
                    <p className="text-gray-600 mb-2">What We Need:</p>
                    {service.id === 'general-consulting' ? (
                      <ul className="space-y-2">
                        <li className="text-sm text-gray-700 flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                          <span>12 months electricity bills</span>
                        </li>
                        <li className="text-sm text-gray-700 flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                          <span>Basic facility details</span>
                        </li>
                        <li className="text-sm text-gray-700 flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                          <span>Renewable energy goals</span>
                        </li>
                      </ul>
                    ) : (
                      <ul className="space-y-2">
                        {service.deliverables.slice(0, 3).map((item) => (
                          <li key={item} className="text-sm text-gray-700 flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  {service.id === 'general-consulting' && (
                    <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                      <p className="text-sm text-emerald-800 font-medium mb-2">🤖 AI-Powered Analysis</p>
                      <p className="text-xs text-emerald-700">
                        Our advanced AI system will analyze your consumption patterns and match you with the optimal solution and trusted partners
                      </p>
                    </div>
                  )}
                </div>

                <Link
                  to="/contact"
                  className="block w-full px-6 py-4 bg-emerald-600 text-white font-semibold rounded-lg hover:bg-emerald-700 text-center transform hover:scale-105 transition-all duration-200 shadow-md"
                >
                  Request for Quote
                </Link>

                <p className="text-xs text-gray-500 text-center mt-4">
                  Get a detailed proposal within 2-3 business days
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Other Services */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Other Renewable Services</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {otherServices.map((s) => {
              const ServiceIcon = iconMap[s.icon];
              return (
                <Link
                  key={s.id}
                  to={`/renewable/${s.slug}`}
                  className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
                >
                  <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center mb-4">
                    <ServiceIcon className="w-6 h-6 text-emerald-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{s.title}</h3>
                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">{s.description}</p>
                  <div className="flex items-center text-emerald-600 font-medium text-sm">
                    Learn More
                    <ArrowRight className="w-4 h-4 ml-1" />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default RenewableServiceDetail;
